import type { AgendaItem } from "@elation/api-client/src/stitch-web/models/agendaItem";
import { AgendaItemSignalState } from "@elation/api-client/src/stitch-web/models/agendaItemSignalState";
import { AgendaItemStatus } from "@elation/api-client/src/stitch-web/models/agendaItemStatus";

import {
  GENERATED_ARTIFACT_POLL_INTERVAL_MS,
  type GeneratedArtifactStatus,
  type GeneratedArtifactViewState,
  generatedArtifactPollIntervalMs,
  isGeneratedArtifactStillPending,
  resolveGeneratedArtifactViewState,
} from "@src/shared/domain/generated-artifact";
import { parseNullableDate } from "@src/shared/utils/dates";

type AgendaItemDateField =
  | "completedAt"
  | "discussedAt"
  | "assessedAt"
  | "plannedAt"
  | "followUpAt"
  | "createdAt"
  | "updatedAt";
type MaybeAgendaItem = AgendaItem | null | undefined;

export const AGENDA_ITEMS_POLL_INTERVAL_MS =
  GENERATED_ARTIFACT_POLL_INTERVAL_MS;

export type AgendaItemListArtifact = {
  items: AgendaItem[];
  artifactStatus?: GeneratedArtifactStatus;
};

const STATUS_LABELS: Record<AgendaItem["status"], string> = {
  [AgendaItemStatus.suggested]: "Suggested",
  [AgendaItemStatus.open]: "Open",
  [AgendaItemStatus.actively_discussing]: "Actively discussing",
  [AgendaItemStatus.finalized]: "Finalized",
  [AgendaItemStatus.rejected]: "Rejected",
  [AgendaItemStatus.cancelled]: "Cancelled",
  [AgendaItemStatus.snoozed]: "Snoozed",
};

const SIGNAL_STATE_LABELS: Record<AgendaItem["signalState"], string> = {
  [AgendaItemSignalState.open]: "Open",
  [AgendaItemSignalState.discussed]: "Discussed",
  [AgendaItemSignalState.assessed]: "Assessed",
  [AgendaItemSignalState.planned]: "Planned",
  [AgendaItemSignalState.follow_up]: "Follow-up",
};

/**
 * How far the AI matcher has carried an item, collapsed into the states a row
 * actually renders differently. `signalState` alone is not enough: completion
 * also depends on `status`, and a clinician-finalized item must be
 * distinguishable from one the matcher closed on its own.
 */
export type AgendaItemProgressState =
  | "open"
  | "discussed"
  | "assessed"
  | "follow_up"
  | "completed"
  | "completed_by_ai";

/** Suffix appended to a row's accessible name — what a screen reader hears. */
const PROGRESS_STATE_LABELS: Record<AgendaItemProgressState, string> = {
  open: "open",
  discussed: "discussed",
  assessed: "assessed",
  follow_up: "follow-up planned for next visit",
  completed: "completed",
  completed_by_ai: "completed by AI",
};

/**
 * Short visible badge text. `open` and `completed` are absent deliberately —
 * the checkbox already carries both, and a badge repeating it is noise on
 * every row of a long agenda.
 */
const PROGRESS_BADGE_LABELS: Partial<Record<AgendaItemProgressState, string>> =
  {
    discussed: "Discussed",
    assessed: "Assessed",
    follow_up: "Follow-up",
    completed_by_ai: "AI",
  };

/**
 * Mirrors the server's `AgendaItemDescriptionField` bound, which both the
 * create and update endpoints enforce. Kept here so the inputs that write a
 * description can stop a paste at the limit instead of letting it become an
 * opaque 400 that silently rolls the optimistic update back.
 */
export const AGENDA_ITEM_DESCRIPTION_MAX_LENGTH = 1000;

function compareDatesDesc(left: Date | null, right: Date | null): number {
  if (left && right) return right.getTime() - left.getTime();
  if (left) return -1;
  if (right) return 1;
  return 0;
}

export const AgendaItemEntity = {
  isCompleted(item: MaybeAgendaItem): boolean {
    if (!item) return false;
    return (
      item.status === AgendaItemStatus.finalized ||
      item.signalState === AgendaItemSignalState.planned
    );
  },

  isPlanned(item: MaybeAgendaItem): boolean {
    if (!item) return false;
    return (
      item.signalState === AgendaItemSignalState.planned ||
      Boolean(parseNullableDate(item.plannedAt))
    );
  },

  needsFollowUp(item: MaybeAgendaItem): boolean {
    if (!item) return false;
    return (
      !AgendaItemEntity.isCompleted(item) &&
      (item.signalState === AgendaItemSignalState.follow_up ||
        Boolean(parseNullableDate(item.followUpAt)))
    );
  },

  isActionable(item: MaybeAgendaItem): boolean {
    if (!item) return false;
    return (
      item.status !== AgendaItemStatus.rejected &&
      item.status !== AgendaItemStatus.cancelled &&
      item.status !== AgendaItemStatus.snoozed
    );
  },

  statusLabel(item: MaybeAgendaItem): string | null {
    if (!item) return null;
    return STATUS_LABELS[item.status];
  },

  signalStateLabel(item: MaybeAgendaItem): string | null {
    if (!item) return null;
    return SIGNAL_STATE_LABELS[item.signalState];
  },

  progressState(item: MaybeAgendaItem): AgendaItemProgressState {
    if (!item) return "open";

    if (AgendaItemEntity.isCompleted(item)) {
      return item.status === AgendaItemStatus.finalized
        ? "completed"
        : "completed_by_ai";
    }

    if (AgendaItemEntity.needsFollowUp(item)) return "follow_up";
    if (item.signalState === AgendaItemSignalState.assessed) return "assessed";
    if (item.signalState === AgendaItemSignalState.discussed)
      return "discussed";
    return "open";
  },

  progressBadgeLabel(item: MaybeAgendaItem): string | null {
    if (!item) return null;
    return PROGRESS_BADGE_LABELS[AgendaItemEntity.progressState(item)] ?? null;
  },

  listItemLabel(item: MaybeAgendaItem): string | null {
    if (!item) return null;
    const progress =
      PROGRESS_STATE_LABELS[AgendaItemEntity.progressState(item)];
    return `${item.description} — ${progress}`;
  },

  dateFor(item: MaybeAgendaItem, field: AgendaItemDateField): Date | null {
    return parseNullableDate(item?.[field]);
  },

  timelineDate(item: MaybeAgendaItem): Date | null {
    if (!item) return null;
    if (item.signalState === AgendaItemSignalState.follow_up) {
      return AgendaItemEntity.dateFor(item, "followUpAt");
    }

    if (item.signalState === AgendaItemSignalState.planned) {
      return AgendaItemEntity.dateFor(item, "plannedAt");
    }

    if (item.signalState === AgendaItemSignalState.assessed) {
      return AgendaItemEntity.dateFor(item, "assessedAt");
    }

    if (item.signalState === AgendaItemSignalState.discussed) {
      return AgendaItemEntity.dateFor(item, "discussedAt");
    }

    return (
      AgendaItemEntity.dateFor(item, "updatedAt") ??
      AgendaItemEntity.dateFor(item, "createdAt")
    );
  },

  compareByTimelineDesc(left: AgendaItem, right: AgendaItem): number {
    const byTimeline = compareDatesDesc(
      AgendaItemEntity.timelineDate(left),
      AgendaItemEntity.timelineDate(right),
    );

    if (byTimeline !== 0) return byTimeline;
    return left.id - right.id;
  },

  artifactStatus(
    response: Pick<AgendaItemListArtifact, "artifactStatus"> | null | undefined,
  ): GeneratedArtifactStatus | undefined {
    return response?.artifactStatus;
  },

  agendaItemsPollIntervalMs(
    artifactStatus: GeneratedArtifactStatus | null | undefined,
  ): typeof AGENDA_ITEMS_POLL_INTERVAL_MS | false {
    return generatedArtifactPollIntervalMs(
      artifactStatus,
      AGENDA_ITEMS_POLL_INTERVAL_MS,
    );
  },

  resolveViewState(input: {
    artifactStatus?: GeneratedArtifactStatus | null;
    itemCount: number;
    isInitialLoading: boolean;
    isFetchError: boolean;
  }): GeneratedArtifactViewState {
    return resolveGeneratedArtifactViewState(input);
  },

  isStillGenerating(input: {
    artifactStatus?: GeneratedArtifactStatus | null;
    itemCount: number;
  }): boolean {
    return isGeneratedArtifactStillPending(input);
  },
} as const;

export type { AgendaItemDateField };
